import React, { useCallback } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import db from '../../lib/db';
import { cn } from '../../lib/utils';
import { versionedWidgetRuntimeUrl } from '../../lib/widgetRuntimeUrl';

import SpinnerIcon from '../../../assets/images/spinner.svg';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import WidgetEmbed from './WidgetEmbed';

const statusCopy = {
  failed: 'Widget failed.',
};

const templateRuntimeCopy = {
  failed: 'Widget unavailable.',
  provisioning: 'Setting up widget.',
  unpublished: 'Widget not published.',
};

const widgetCardClassName =
  'relative overflow-hidden rounded-[12px] border-[0.5px] border-gray-200 bg-white shadow-[0px_9px_32px_0px_rgba(0,0,0,0.2)] dark:border-neutral-800 dark:bg-neutral-900';

// Per-template card chrome, keyed by template slug. Opt-in: a template with no entry renders
// the default above, so adding one here never changes any other widget. These merge through
// cn(), so a border-* class replaces the default's rather than racing it in the stylesheet.
const widgetCardOverrides = Object.freeze({
  // The assistant paints its own dark surface, so it carries a stronger stroke than the
  // hairline the light-bodied widgets need. The dark variant is stated explicitly: without it
  // the base's dark:border-neutral-800 would survive the merge and only light mode would change.
  assistant: 'border-gray-500 dark:border-gray-500',
});

const WidgetCard = React.memo(function WidgetCard({ templateSlug, children, ...props }) {
  return (
    <article {...props} className={cn(widgetCardClassName, templateSlug && widgetCardOverrides[templateSlug])}>
      {children}
    </article>
  );
});

const Widget = React.memo(function Widget({
  widget,
  wrapperClassName,
  canRemove = false,
  canEdit = false,
  onRemove,
  onEdit,
  onMoveRequest,
  canMoveUp = false,
  canMoveDown = false,
  orderingControls,
  hideActionsRow = false,
  lockEmbedHeight = false,
  cornerAction = null,
  viewerUnavailableReason: viewerUnavailableReasonOverride,
  ...props
}) {
  const published = widget?.status === 'published';
  const templateRuntimeState = widget?.template_runtime_state;
  const templateRuntimeText = templateRuntimeCopy[templateRuntimeState];
  const templateRuntimeBlocksEmbed = Boolean(templateRuntimeState && templateRuntimeState !== 'published');
  const runtimeVersion = published ? widget?.published_version : widget?.highest_version;
  const embedUrl = versionedWidgetRuntimeUrl(published ? widget?.url : widget?.preview_url, runtimeVersion);
  const embedHeight = published ? widget?.height : widget?.preview_height;
  const hasUrl = !!embedUrl;
  const shouldRenderEmbed = hasUrl && !templateRuntimeBlocksEmbed;
  const fallbackText = templateRuntimeText || statusCopy[widget?.status] || 'Widget pending.';
  const title = widget?.title || 'Widget';
  const widgetId = widget?.id;
  const height = embedHeight || 50;
  const ownerProfileId = widget?.profile_id;
  const { workspaceId, profile: currentProfile } = useRouteWorkspace();
  const ownerProfile = useLiveQuery(
    () => (workspaceId && ownerProfileId ? db.profiles.get([workspaceId, ownerProfileId]) : null),
    [ownerProfileId, workspaceId],
  );
  const ownerFullName = ownerProfile?.full_name;
  const isOwnedByCurrentProfile = Boolean(ownerProfileId && ownerProfileId === currentProfile?.id);
  // Viewer availability normally derives from whether a profile is present. But callers that
  // render in a `requireProfile={false}` tree where a viewer session must NEVER be minted (the
  // shared-channel sidebar) pass an explicit `viewerUnavailableReason` override and we honor it
  // verbatim. We must not derive availability from `currentProfile` there: a workspace member
  // viewing the public share link resolves a real profile (RouteWorkspaceProvider still matches
  // one when `requireProfile={false}`), which would otherwise mint a viewer session on a shared
  // page. v1 keeps shadows session-less — see docs/widget-runtime-auth.md "Out of scope". Absent
  // an override (the authenticated app), fall back to deriving it from the profile.
  const viewerUnavailableReason = viewerUnavailableReasonOverride ?? (currentProfile?.id ? null : 'no_profile');
  const canShowEdit = Boolean(canEdit && onEdit && widgetId);
  // Edit owns the right-side action slot when present; the owner name is shown as fallback context for non-owners only (admins editing someone else's widget see Edit, not the owner name).
  const canShowOwnerName = Boolean(!canShowEdit && !isOwnedByCurrentProfile && ownerFullName);
  const canHandleRemoveRequest = Boolean(canRemove && onRemove && widgetId);
  const canHandleMoveRequest = Boolean(onMoveRequest && widgetId);
  const hasLeftActions = Boolean(orderingControls);
  const showActionsRow = Boolean(hasLeftActions || canShowEdit || canShowOwnerName);

  const handleEdit = useCallback(() => onEdit?.(widgetId), [onEdit, widgetId]);
  const handleRemove = useCallback(() => onRemove?.(widget), [onRemove, widget]);
  const handleMoveRequest = useCallback(
    (direction) => {
      if (direction === 'up') {
        if (canMoveUp) onMoveRequest?.('up');
        return;
      }

      if (direction === 'down') {
        if (canMoveDown) onMoveRequest?.('down');
      }
    },
    [canMoveDown, canMoveUp, onMoveRequest],
  );

  return (
    <div className={cn('group w-82', wrapperClassName)} {...props}>
      {/* Sibling of the card, not inside it: edit mode disables the card's
          pointer events, and a corner control has to stay clickable. */}
      {cornerAction}
      <WidgetCard data-widget-id={widgetId} templateSlug={widget?.template_slug}>
        {shouldRenderEmbed ? (
          <WidgetEmbed
            title={title}
            src={embedUrl}
            height={height}
            agentChatId={widget?.agent_chat_id}
            workspaceId={workspaceId}
            widgetId={widgetId}
            viewerSessionEnabled={widget?.viewer_session_enabled}
            requiresViewer={widget?.requires_viewer}
            viewerUnavailableReason={viewerUnavailableReason}
            onDeleteRequest={canHandleRemoveRequest ? handleRemove : undefined}
            canMoveUp={canHandleMoveRequest && canMoveUp}
            canMoveDown={canHandleMoveRequest && canMoveDown}
            onMoveRequest={canHandleMoveRequest ? handleMoveRequest : undefined}
            lockHeight={lockEmbedHeight}
          />
        ) : (
          <div
            className={cn(
              'flex w-full items-center justify-center bg-gray-50 px-4 text-center text-sm text-gray-500 dark:bg-neutral-950 dark:text-neutral-400',
              (widget?.status === 'failed' || templateRuntimeState === 'failed') && 'text-red-600 dark:text-red-400',
            )}
            role={templateRuntimeState === 'provisioning' || widget?.status === 'processing' ? 'status' : undefined}
            style={{ height }}
          >
            {templateRuntimeState === 'provisioning' ? (
              <div className="flex min-w-0 items-center justify-center gap-2">
                <SpinnerIcon className="size-4 shrink-0 animate-spin text-gray-500 dark:text-neutral-400" />
                <span className="min-w-0">{fallbackText}</span>
              </div>
            ) : widget?.status === 'processing' ? (
              <SpinnerIcon className="size-4 animate-spin text-gray-500 dark:text-neutral-400" />
            ) : (
              fallbackText
            )}
          </div>
        )}
      </WidgetCard>

      {!hideActionsRow && showActionsRow ? (
        <div className="mt-3 flex min-w-0 items-center px-3">
          {hasLeftActions ? (
            <div className="flex shrink-0 items-center gap-2">
              {orderingControls ? <div className="flex shrink-0 items-center">{orderingControls}</div> : null}
            </div>
          ) : null}
          {canShowEdit ? (
            <button
              type="button"
              onClick={handleEdit}
              className="ml-auto cursor-pointer text-right font-sans text-[10px] leading-normal font-[420] text-gray-500 hover:underline"
            >
              Edit
            </button>
          ) : canShowOwnerName ? (
            <div className="ml-auto min-w-0 truncate text-right font-sans text-[10px] leading-normal font-[420] text-gray-500">
              {ownerFullName}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});

export default Widget;
